'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT } from '@/sanity/types';
import type { Geolocation } from '@/lib/server/geolocation/types';
import { filterLocationsByRadius, filterLocationsByRegion, filterLocationsByAmenity, normalizeRegionName } from '@/lib/utils/locationSearch';
export type FilterState = {
  selectedRegions: string[];
  selectedAmenities: string[]; // Changed from selectedAccessFeatures to selectedAmenities (using amenity IDs)
  searchLocation: Geolocation | null;
  searchRadius: number;
};
const DEFAULT_RADIUS = 30;
export function useLocationFilters(locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT, defaultSelectedRegions: string[] = []) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Initialize state from URL params
  const getInitialState = useCallback((): FilterState => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const address = searchParams.get('address');
    const regionsParam = searchParams.get('regions');
    const accessFeaturesParam = searchParams.get('accessFeatures');
    const radiusParam = searchParams.get('radius');
    let searchLocation: Geolocation | null = null;
    if (lat && lng && address) {
      searchLocation = {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        formattedAddress: address,
        city: '',
        subdivision: {
          code: '',
          name: ''
        },
        country: {
          code: 'GB',
          name: 'United Kingdom'
        },
        postalCode: ''
      };
    }
    const initialSelectedRegionsRaw = regionsParam ? regionsParam.split(',') : defaultSelectedRegions;
    const initialSelectedRegions = initialSelectedRegionsRaw.map(name => normalizeRegionName(name)).filter(name_0 => name_0.length > 0);
    return {
      selectedRegions: initialSelectedRegions,
      selectedAmenities: accessFeaturesParam ? accessFeaturesParam.split(',') : [],
      searchLocation,
      searchRadius: radiusParam ? parseInt(radiusParam, 10) : DEFAULT_RADIUS
    };
  }, [searchParams, defaultSelectedRegions]);
  const [filters, setFilters] = useState<FilterState>(getInitialState);
  const isInitialMount = useRef(true);
  const lastWrittenURLRef = useRef<string>('');
  const previousFiltersRef = useRef<FilterState>(filters);

  // Helper to check if filters actually changed
  const filtersChanged = useCallback((prev: FilterState, next: FilterState): boolean => {
    return JSON.stringify(prev.selectedRegions) !== JSON.stringify(next.selectedRegions) || JSON.stringify(prev.selectedAmenities) !== JSON.stringify(next.selectedAmenities) || prev.searchRadius !== next.searchRadius || prev.searchLocation?.latitude !== next.searchLocation?.latitude || prev.searchLocation?.longitude !== next.searchLocation?.longitude || prev.searchLocation?.formattedAddress !== next.searchLocation?.formattedAddress;
  }, []);

  // Helper to build URL string from filters
  const buildURLFromFilters = useCallback((filterState: FilterState): string => {
    const params = new URLSearchParams();
    if (filterState.searchLocation) {
      params.set('lat', filterState.searchLocation.latitude.toString());
      params.set('lng', filterState.searchLocation.longitude.toString());
      params.set('address', filterState.searchLocation.formattedAddress || '');
    }

    // Only write regions param if it differs from defaultSelectedRegions,
    // so implicit preselection doesn't appear in the URL.
    const normalize = (values: string[]) => [...values].map(v => v.toLowerCase()).sort().join(',');
    const defaultRegionsKey = normalize(defaultSelectedRegions);
    const selectedRegionsKey = normalize(filterState.selectedRegions);
    const regionsDifferFromDefault = selectedRegionsKey.length > 0 && selectedRegionsKey !== defaultRegionsKey;
    if (regionsDifferFromDefault) {
      params.set('regions', filterState.selectedRegions.join(','));
    }
    if (filterState.selectedAmenities.length > 0) {
      params.set('accessFeatures', filterState.selectedAmenities.join(','));
    }
    if (filterState.searchRadius !== DEFAULT_RADIUS) {
      params.set('radius', filterState.searchRadius.toString());
    }
    return params.toString();
  }, []);

  // Initialize lastWrittenURLRef on mount
  useEffect(() => {
    if (isInitialMount.current) {
      const initialURL = buildURLFromFilters(filters);
      lastWrittenURLRef.current = initialURL;
      isInitialMount.current = false;
      previousFiltersRef.current = filters;
    }
  }, [filters, buildURLFromFilters]);

  // Sync state when URL params change (e.g., from HeroSliderBanner)
  // Only update if the URL params changed from an external source (not our own updates)
  useEffect(() => {
    const currentURL = searchParams.toString();

    // Skip if this is the URL we just wrote (to prevent loops)
    if (currentURL === lastWrittenURLRef.current) {
      return;
    }
    const newState = getInitialState();
    setFilters(prev_0 => {
      if (filtersChanged(prev_0, newState)) {
        previousFiltersRef.current = newState;
        lastWrittenURLRef.current = currentURL;
        return newState;
      }
      return prev_0;
    });
  }, [getInitialState, filtersChanged, searchParams]);

  // Apply defaultSelectedRegions once they become available, but only
  // when there is no explicit regions param in the URL.
  useEffect(() => {
    const regionsParam_0 = searchParams.get('regions');
    if (regionsParam_0) {
      return;
    }
    if (defaultSelectedRegions.length === 0) {
      return;
    }
    setFilters(prev_1 => {
      const normalize_0 = (values_0: string[]) => [...values_0].map(v_0 => v_0.toLowerCase()).sort().join(',');
      const currentKey = normalize_0(prev_1.selectedRegions);
      const defaultKey = normalize_0(defaultSelectedRegions);
      if (currentKey === defaultKey) {
        return prev_1;
      }
      return {
        ...prev_1,
        selectedRegions: defaultSelectedRegions
      };
    });
  }, [defaultSelectedRegions, searchParams]);

  // Sync URL params when filter state changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    // Only update if filters actually changed from previous render
    if (!filtersChanged(previousFiltersRef.current, filters)) {
      return;
    }
    const newURL = buildURLFromFilters(filters);
    const currentURL_0 = searchParams.toString();

    // Only update if URL is different
    if (newURL !== currentURL_0) {
      previousFiltersRef.current = filters;
      lastWrittenURLRef.current = newURL;
      router.replace(`${pathname}?${newURL}#search-results`, {
        scroll: false
      });
    } else {
      previousFiltersRef.current = filters;
    }
  }, [filters, filtersChanged, buildURLFromFilters, router, searchParams]);
  const toggleRegion = useCallback((regionName: string) => {
    const normalizedRegionName = normalizeRegionName(regionName);
    setFilters(prev_2 => {
      const newRegions = prev_2.selectedRegions.includes(normalizedRegionName) ? prev_2.selectedRegions.filter(r => r !== normalizedRegionName) : [...prev_2.selectedRegions, normalizedRegionName];
      return {
        ...prev_2,
        selectedRegions: newRegions
      };
    });
  }, []);
  const toggleAmenity = useCallback((amenityId: string) => {
    setFilters(prev_3 => {
      const newAmenities = prev_3.selectedAmenities.includes(amenityId) ? prev_3.selectedAmenities.filter(id => id !== amenityId) : [...prev_3.selectedAmenities, amenityId];
      return {
        ...prev_3,
        selectedAmenities: newAmenities
      };
    });
  }, []);
  const setSearchLocation = useCallback((location: Geolocation | null) => {
    setFilters(prev_4 => ({
      ...prev_4,
      searchLocation: location
    }));
  }, []);
  const setSearchRadius = useCallback((radius: number) => {
    setFilters(prev_5 => ({
      ...prev_5,
      searchRadius: radius
    }));
  }, []);
  const clearFilters = useCallback(() => {
    setFilters({
      selectedRegions: defaultSelectedRegions,
      selectedAmenities: [],
      searchLocation: null,
      searchRadius: DEFAULT_RADIUS
    });
  }, [defaultSelectedRegions]);
  const filteredLocations = useMemo(() => {
    let result = [...locations];

    // Filter by regions
    if (filters.selectedRegions.length > 0) {
      result = filterLocationsByRegion(result, filters.selectedRegions);
    }

    // Filter by radius if search location is set
    if (filters.searchLocation) {
      const radiusFiltered = filterLocationsByRadius(result, filters.searchLocation, filters.searchRadius);
      // Convert back to ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT but keep distance info
      // Note: Results are already sorted by distance from filterLocationsByRadius
      result = radiusFiltered as unknown as ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT;
    } else {
      // Sort alphabetically by location name when no search location is provided
      result = [...result].sort((a, b) => {
        const nameA = (a as any).locationName || '';
        const nameB = (b as any).locationName || '';
        // Handle null/undefined by putting them at the end
        if (!nameA && !nameB) return 0;
        if (!nameA) return 1;
        if (!nameB) return -1;
        return nameA.localeCompare(nameB, undefined, {
          sensitivity: 'base'
        });
      });
    }

    // Filter by amenities
    if (filters.selectedAmenities.length > 0) {
      result = filterLocationsByAmenity(result, filters.selectedAmenities);
    }
    return result;
  }, [locations, filters]);
  return {
    filters,
    filteredLocations,
    toggleRegion,
    toggleAmenity,
    setSearchLocation,
    setSearchRadius,
    clearFilters
  };
}